import * as yup from "yup";

import {
    AUTO_START_COUNTDOWN_RANGE,
    GAME_TIME_RANGE,
    MAX_QUIZ_OPTIONS,
    PAIR_COUNT_RANGE,
    PUZZLE_COUNT_RANGE,
    QUIZ_PASS_RANGE,
    QUIZ_TOTAL_RANGE,
} from "../constants";

// =============== Quiz Option Schema ================
const quizOptionSchema = yup.object({
    text: yup
        .string()
        .trim()
        .required("APPWIDGET.GAME_VALIDATION_OPTION_TEXT_REQUIRED")
        .min(1, "APPWIDGET.GAME_VALIDATION_OPTION_TEXT_REQUIRED"),
    image_url: yup.string().nullable(),
});
// ============ Item Schemas ============
const puzzleItemSchema = yup.object({
    id: yup.string().required(),
    image_url: yup.string().default(""),
    title: yup.string().default(""),
    description: yup.string().default(""),
});

const quizItemSchema = yup.object({
    id: yup.string().required(),
    title: yup
        .string()
        .trim()
        .required("APPWIDGET.GAME_VALIDATION_TITLE_REQUIRED")
        .min(1, "APPWIDGET.GAME_VALIDATION_TITLE_REQUIRED"),
    image_url: yup.string().nullable().default(""),
    options: yup
        .array()
        .of(quizOptionSchema)
        .min(MAX_QUIZ_OPTIONS, "APPWIDGET.GAME_VALIDATION_OPTIONS_COUNT")
        .max(MAX_QUIZ_OPTIONS, "APPWIDGET.GAME_VALIDATION_MAX_OPTIONS")
        .default([]),
    correct_option_index: yup
        .number()
        .min(0)
        .max(MAX_QUIZ_OPTIONS - 1)
        .default(0),
});

const flipCardItemSchema = yup.object({
    id: yup.string().required(),
    image_url: yup.string().default(""),
    title: yup.string().default(""),
    description: yup.string().default(""),
});

const prizeItemSchema = yup.object({
    id: yup.string().required(),
    title: yup.string().default(""),
    type: yup.string().oneOf(["none", "qrcode", "image"]).default("none"),
    url: yup.string().default(""),
});
// ============ Language Content Schema Factory ============
const createLanguageContentSchema = <T extends yup.Maybe<yup.AnyObject>>(itemSchema: yup.ObjectSchema<T>) =>
    yup.object({
        lang: yup.string().required(),
        content: yup.array().of(itemSchema).default([]),
    });

// ============ Game Settings Schemas ============
const puzzleSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    game_time: yup.number().min(GAME_TIME_RANGE.min).max(GAME_TIME_RANGE.max).default(GAME_TIME_RANGE.default),
    puzzle_count: yup
        .number()
        .min(PUZZLE_COUNT_RANGE.min)
        .max(PUZZLE_COUNT_RANGE.max)
        .default(PUZZLE_COUNT_RANGE.default),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: yup.array().of(createLanguageContentSchema(puzzleItemSchema)).required(),
});

const quizSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    total_per_round: yup.number().min(QUIZ_TOTAL_RANGE.min).max(QUIZ_TOTAL_RANGE.max).default(QUIZ_TOTAL_RANGE.default),
    pass_count: yup
        .number()
        .min(QUIZ_PASS_RANGE.min)
        .max(QUIZ_PASS_RANGE.max)
        .default(QUIZ_PASS_RANGE.default)
        .when("total_per_round", {
            is: (total: number) => total !== undefined,
            then: (schema) =>
                schema.test(
                    "pass-count-not-exceed-total",
                    "APPWIDGET.GAME_VALIDATION_PASS_COUNT_EXCEED_TOTAL",
                    function (passCount) {
                        const { total_per_round } = this.parent;
                        if (passCount === undefined || total_per_round === undefined) return true;
                        return passCount <= total_per_round;
                    }
                ),
        }),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: yup.array().of(createLanguageContentSchema(quizItemSchema)).required(),
});

const flipCardSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    game_time: yup.number().min(GAME_TIME_RANGE.min).max(GAME_TIME_RANGE.max).default(GAME_TIME_RANGE.default),
    pair_count: yup.number().min(PAIR_COUNT_RANGE.min).max(PAIR_COUNT_RANGE.max).default(PAIR_COUNT_RANGE.default),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: yup.array().of(createLanguageContentSchema(flipCardItemSchema)).required(),
});

const prizeCommonSchema = yup.object({
    enabled: yup.boolean().default(false),
    mode: yup.string().oneOf(["wheel", "slot"]).default("wheel"),
    auto_start_countdown: yup
        .number()
        .min(AUTO_START_COUNTDOWN_RANGE.min)
        .max(AUTO_START_COUNTDOWN_RANGE.max)
        .default(AUTO_START_COUNTDOWN_RANGE.default),
});

const prizeSettingsSchema = yup.object({
    common: prizeCommonSchema,
    data: yup.array().of(createLanguageContentSchema(prizeItemSchema)).required(),
});

// ============ Main Form Schema ============
export const gameSettingsValidationSchema = yup.object({
    enabled: yup.boolean().default(false),
    games: yup.object({
        puzzle: puzzleSettingsSchema,
        quiz: quizSettingsSchema,
        flipcard: flipCardSettingsSchema,
    }),
    prize: prizeSettingsSchema,
});

export type GameSettingsFormSchema = yup.InferType<typeof gameSettingsValidationSchema>;
